import { useState, useEffect } from "react";
import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Input, Select, Button } from "antd";
import {
  UsergroupAddOutlined, MailOutlined, ApiOutlined, EditOutlined, CloseOutlined, CopyOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import NodeNameInput from "./NodeNameInput";

const { Option } = Select;

const ICONS = {
  addToGroup:      <UsergroupAddOutlined style={{ color: "#10b981" }} />,
  removeFromGroup: <UsergroupAddOutlined style={{ color: "#ef4444" }} />,
  updateContact:   <EditOutlined        style={{ color: "#f59e0b" }} />,
  sendEmail:       <MailOutlined        style={{ color: "#3b82f6" }} />,
  webhook:         <ApiOutlined         style={{ color: "#8b5cf6" }} />,
};

const TITLES = {
  addToGroup:      "Add to Group",
  removeFromGroup: "Remove from Group",
  updateContact:   "Update Contact",
  sendEmail:       "Send Email",
  webhook:         "Webhook",
};

const CONTACT_FIELDS = [
  { value: "status",      label: "Status" },
  { value: "notes",       label: "Note" },
  { value: "designation", label: "Designation" },
  { value: "company",     label: "Company" },
  { value: "website",     label: "Website" },
];

const ActionNode = ({ data, selected, type, id }) => {
  const { deleteElements } = useReactFlow();
  const allTags = useSelector((s) => s.tags.tags) || [];
  const onChange = (field, value) => data.onChange?.({ ...data, [field]: value });

  // Local state for JSON textareas — prevents focus loss on every keystroke
  const [headersRaw, setHeadersRaw] = useState(data.headersRaw || "");
  const [bodyRaw, setBodyRaw]       = useState(data.bodyRaw || "");
  useEffect(() => { setHeadersRaw(data.headersRaw || ""); }, [data.headersRaw]);
  useEffect(() => { setBodyRaw(data.bodyRaw || "");       }, [data.bodyRaw]);

  const commitHeaders = (raw) => {
    let parsed = {};
    try { parsed = JSON.parse(raw || "{}"); } catch (_) {}
    data.onChange?.({ ...data, headersRaw: raw, headers: parsed });
  };
  const commitBody = (raw) => {
    let parsed = {};
    try { parsed = JSON.parse(raw || "{}"); } catch (_) {}
    data.onChange?.({ ...data, bodyRaw: raw, body: parsed });
  };

  const color = {
    addToGroup:      "#10b981",
    removeFromGroup: "#ef4444",
    updateContact:   "#f59e0b",
    sendEmail:       "#3b82f6",
    webhook:         "#8b5cf6",
  }[type] || "#6b7280";

  return (
    <div style={{
      background: "#fff",
      border: `2px solid ${selected ? color : "#e5e7eb"}`,
      borderRadius: 10,
      padding: 14,
      minWidth: 260,
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
    }}>
      <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color={color} />
      <Handle type="target" position={Position.Left} style={{ background: color }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        {ICONS[type]}
        <strong style={{ fontSize: 13 }}>{TITLES[type]}</strong>
        <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => data.onDuplicate?.()} style={{ marginLeft: "auto", color: "#9ca3af" }} />
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => deleteElements({ nodes: [{ id }] })} style={{ color: "#9ca3af" }} />
      </div>

      <div
        className="nodrag"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <NodeNameInput data={data} />
        {/* Add / Remove from Group */}
        {(type === "addToGroup" || type === "removeFromGroup") && (
          <>
            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Group</label>
            <Select
              value={data.tagId}
              onChange={(v, opt) => data.onChange?.({ ...data, tagId: v, tagName: opt?.label })}
              size="small"
              style={{ width: "100%" }}
              placeholder="Select group"
              options={allTags.map((t) => ({ value: t.tag_id, label: `${t.emoji || ""} ${t.tag}` }))}
            />
          </>
        )}

        {/* Update Contact */}
        {type === "updateContact" && (
          <>
            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Field</label>
            <Select
              value={data.field}
              onChange={(v) => onChange("field", v)}
              size="small"
              style={{ width: "100%", marginBottom: 8 }}
              placeholder="Select field"
              options={CONTACT_FIELDS}
            />
            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Value</label>
            <Input
              value={data.value || ""}
              onChange={(e) => onChange("value", e.target.value)}
              size="small"
              placeholder="Enter value"
            />
          </>
        )}

        {/* Send Email */}
        {type === "sendEmail" && (
          <>
            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Subject</label>
            <Input
              value={data.subject || ""}
              onChange={(e) => onChange("subject", e.target.value)}
              size="small"
              style={{ marginBottom: 8 }}
            />
            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Body</label>
            <Input.TextArea
              value={data.body || ""}
              onChange={(e) => onChange("body", e.target.value)}
              rows={2}
              style={{ fontSize: 12 }}
            />
          </>
        )}

        {/* Webhook */}
        {type === "webhook" && (
          <>
            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>URL</label>
            <Input
              value={data.url || ""}
              onChange={(e) => onChange("url", e.target.value)}
              size="small"
              placeholder="https://..."
              style={{ marginBottom: 8 }}
            />
            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Method</label>
            <Select
              value={data.method || "POST"}
              onChange={(v) => onChange("method", v)}
              size="small"
              style={{ width: "100%", marginBottom: 8 }}
            >
              <Option value="POST">POST</Option>
              <Option value="GET">GET</Option>
              <Option value="PUT">PUT</Option>
              <Option value="PATCH">PATCH</Option>
              <Option value="DELETE">DELETE</Option>
            </Select>

            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
              Headers (JSON, optional)
            </label>
            <Input.TextArea
              value={headersRaw}
              onChange={(e) => setHeadersRaw(e.target.value)}
              onBlur={(e) => commitHeaders(e.target.value)}
              rows={2}
              size="small"
              placeholder={'{"Authorization": "Bearer token"}'}
              style={{ fontFamily: "monospace", fontSize: 11, marginBottom: 8 }}
            />

            {(data.method || "POST") !== "GET" && (
              <>
                <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
                  Body (JSON, optional)
                </label>
                <Input.TextArea
                  value={bodyRaw}
                  onChange={(e) => setBodyRaw(e.target.value)}
                  onBlur={(e) => commitBody(e.target.value)}
                  rows={3}
                  size="small"
                  placeholder={'{"key": "value"}'}
                  style={{ fontFamily: "monospace", fontSize: 11, marginBottom: 4 }}
                />
                <div style={{ fontSize: 10, color: "#9ca3af", lineHeight: 1.4, marginBottom: 8 }}>
                  Contact data (phone, name, email…) auto-injected under <code>contact</code> key.
                </div>
              </>
            )}

            {/* Response branching */}
            <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 8, marginTop: 4 }}>
              <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
                Branch on response?
              </label>
              <Select
                value={data.branchOnResponse ? "yes" : "no"}
                onChange={(v) => onChange("branchOnResponse", v === "yes")}
                size="small"
                style={{ width: "100%", marginBottom: 6 }}
                options={[{ label: "No (single path)", value: "no" }, { label: "Yes (success / failure paths)", value: "yes" }]}
              />
              {data.branchOnResponse && (
                <>
                  <Select
                    value={data.branchMode || "httpStatus"}
                    onChange={(v) => onChange("branchMode", v)}
                    size="small"
                    style={{ width: "100%", marginBottom: 6 }}
                    options={[
                      { label: "HTTP status (2xx = success)", value: "httpStatus" },
                      { label: "JSON field check", value: "jsonField" },
                    ]}
                  />
                  {data.branchMode === "jsonField" && (
                    <>
                      <Input
                        value={data.branchFieldPath || ""}
                        onChange={(e) => onChange("branchFieldPath", e.target.value)}
                        size="small"
                        placeholder="Field path e.g. data.status"
                        style={{ marginBottom: 4 }}
                      />
                      <div style={{ display: "flex", gap: 4 }}>
                        <Select
                          value={data.branchOperator || "eq"}
                          onChange={(v) => onChange("branchOperator", v)}
                          size="small"
                          style={{ flex: 1 }}
                          options={[
                            { label: "equals", value: "eq" },
                            { label: "not equals", value: "ne" },
                            { label: "contains", value: "contains" },
                            { label: "is empty", value: "empty" },
                            { label: "is not empty", value: "notEmpty" },
                          ]}
                        />
                        {!["empty", "notEmpty"].includes(data.branchOperator || "eq") && (
                          <Input
                            value={data.branchValue || ""}
                            onChange={(e) => onChange("branchValue", e.target.value)}
                            size="small"
                            placeholder="value"
                            style={{ flex: 1 }}
                          />
                        )}
                      </div>
                    </>
                  )}
                  <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4 }}>
                    Connect "success" handle → next step. Connect "failure" handle → error path.
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {type === "webhook" && data.branchOnResponse ? (
        <>
          <Handle type="source" position={Position.Right} id="success"
            style={{ background: "#10b981", top: "38%" }}
          />
          <Handle type="source" position={Position.Right} id="failure"
            style={{ background: "#ef4444", top: "62%" }}
          />
          <div style={{ position: "absolute", right: -48, top: "32%", fontSize: 9, color: "#10b981", fontWeight: 700 }}>OK</div>
          <div style={{ position: "absolute", right: -44, top: "56%", fontSize: 9, color: "#ef4444", fontWeight: 700 }}>ERR</div>
        </>
      ) : (
        <Handle type="source" position={Position.Right} style={{ background: color }} />
      )}
    </div>
  );
};

export default ActionNode;
